import React from 'react';
import styled from 'styled-components';
import {Typography} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import SGTitle from 'components/Typography/SGTitle';
import SGSubtitle from 'components/Typography/SGSubtitle';
import SGBody from 'components/Typography/SGBody';
import Device from 'features/POSRegisters/interfaces/Device';
import {
  ReceiptPrinterScanDetails,
  BarCodeScanDetails,
  CashDrawerScanDetails,
} from 'features/POSRegisters/interfaces/ScanDetails';
import Alert from 'features/POSRegisters/components/Alert';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';

//#endregion styles
const StyledContainer = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  padding-top: 8px;
  gap: 16px;
`;

const StyledGrid = styled(Grid)`
  display: grid;
  grid-template-columns: 10rem auto 1fr;
  gap: 16px;
  align-items: center;
  color: ${props => props.theme.text};
  background: ${props => props.theme.disabledStateBlue}33;
  overflow-x: auto;
`;

const StyledRightAlignedText = styled(SGTitle)`
  text-align: right;
`;

const StyledDivider = styled.div`
  height: 100%;
  border-left: 1px solid ${props => props.theme.grey};
`;

const StyledDetails = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 8px 0;
  padding-right: 8px;
`;

const StyledDetailsRow = styled(Grid)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, auto));
  grid-template-rows: auto;
  grid-auto-flow: row;
  gap: 16px;
  justify-content: start;
`;

const StyledDetail = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin: 0;
  text-align: left;
  gap: 4px;
`;
const StyledLabel = styled(SGTitle)`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${props => props.theme.disabledStateBlue};
`;
//#endregion styles

interface ReceiptPrinterSummaryProps extends Device<ReceiptPrinterScanDetails> {
  barcodeScanner: Device<BarCodeScanDetails>;
  cashDrawer: Device<CashDrawerScanDetails>;
}

export default function ReceiptPrinterSummary(props: ReceiptPrinterSummaryProps) {
  const {
    pingStatus,
    deviceName,
    ipAddress,
    macAddress,
    vlanNumber,
    model,
    manufacturer,
    serialNumber,
    switchId,
    switchNumber,
    portNumber,
    printerId,
    printerFirstSeenDate,
    printerLastSeenDate,
  } = props.scanResults ?? {};

  // These are not available at the moment.
  const barcodeScanner = props.barcodeScanner ?? {};
  const cashDrawer = props.cashDrawer ?? {};

  return (
    <StyledContainer>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">CONNECTIVITY</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">PING STATUS</StyledLabel>
              <SGBody className="b2">{pingStatus ?? 'Failure'}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">IP ADDRESS</StyledLabel>
              <SGBody className="b2">{ipAddress}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MAC ADDRESS</StyledLabel>
              <SGBody className="b2">{macAddress}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">VLAN</StyledLabel>
              <SGBody className="b2">{vlanNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">SWITCH ID</StyledLabel>
              <SGBody className="b2">{switchId}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">SWITCH NUMBER</StyledLabel>
              <SGBody className="b2">{switchNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">PORT NUMBER</StyledLabel>
              <SGBody className="b2">{portNumber}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(props.alerts, AlertScanSubType.CONNECTIVITY).map((alert, alertIdx) => (
            <Alert
              key={`${deviceName}-${alertIdx}`}
              device={props}
              alert={alert}
              rows={[
                {label: 'Issue', value: alert.description, fullWidth: true},
                {label: 'Effect', value: alert.effect, fullWidth: true},
                {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
              ]}
            />
          ))}
        </StyledDetails>
      </StyledGrid>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">CHECK-IN STATUS</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">FIRST SEEN DATE</StyledLabel>
              <SGBody className="b2">{printerFirstSeenDate}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">LAST SEEN DATE</StyledLabel>
              <SGBody className="b2">{printerLastSeenDate}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(props.alerts, AlertScanSubType.CHECK_IN_STATUS).map(
            (alert, alertIdx) => (
              <Alert
                key={`${deviceName}-${alertIdx}`}
                device={props}
                alert={alert}
                rows={[
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            )
          )}
        </StyledDetails>
      </StyledGrid>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">INFORMATION</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <SGTitle className="text-color t5">HARDWARE</SGTitle>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">DEVICE NAME</StyledLabel>
              <SGBody className="b2">{deviceName}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MANUFACTURER</StyledLabel>
              <SGBody className="b2">{manufacturer}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MODEL</StyledLabel>
              <SGBody className="b2">{model}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">SERIAL NUMBER</StyledLabel>
              <SGBody className="b2">{serialNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">PRINTER ID</StyledLabel>
              <SGBody className="b2">{printerId}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(props.alerts, AlertScanSubType.HARDWARE).map((alert, alertIdx) => (
            <Alert
              key={`${deviceName}-${alertIdx}`}
              device={props}
              alert={alert}
              rows={[
                {label: 'Issue', value: alert.description, fullWidth: true},
                {label: 'Effect', value: alert.effect, fullWidth: true},
                {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
              ]}
            />
          ))}
        </StyledDetails>
      </StyledGrid>
      {/* <StyledGrid>
        <StyledRightAlignedText className="text-color t5">BARCODE SCANNER</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">NAME</StyledLabel>
              <SGBody className="b2">{barcodeScanner.scanResults?.deviceName}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">DEVICE ID</StyledLabel>
              <SGBody className="b2">{barcodeScanner.scanResults?.deviceId}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">BUS</StyledLabel>
              <SGBody className="b2">{barcodeScanner.scanResults?.bus}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MODEL</StyledLabel>
              <SGBody className="b2">{barcodeScanner.scanResults?.model}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">SERIAL NUMBER</StyledLabel>
              <SGBody className="b2">{barcodeScanner.scanResults?.serialNumber}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>
          {barcodeScanner?.alerts?.map(alert => (
            <Accordion
              title={alert.siteImpact?.toUpperCase()}
              severity={alert.siteImpact}
              arrowPlacement="end"
            >
              <AlertContent>
                <StyledAlertTitle variant="s2" className={alert.siteImpact}>
                  {alert.shortDescription}
                </StyledAlertTitle>
                <StyledTitle>Issue</StyledTitle>
                <StyledText variant="b4">{alert.description}</StyledText>
                <StyledTitle>Effect</StyledTitle>
                <StyledText variant="b4">{alert.effect}</StyledText>
                <StyledTitle>Solution</StyledTitle>
                <StyledText variant="b4">
                  {alert.solution} {alert.kb}
                </StyledText>
              </AlertContent>
            </Accordion>
          ))}
        </StyledDetails>
      </StyledGrid>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">CASH DRAWER</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">tillNumber</StyledLabel>
              <SGBody className="b2">{cashDrawer.scanResults?.tillNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Signal Configuration</StyledLabel>
              <SGBody className="b2">{cashDrawer.scanResults?.signalConfiguration}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>
          {cashDrawer?.alerts?.map(alert => (
            <Accordion
              title={alert.siteImpact?.toUpperCase()}
              severity={alert.siteImpact}
              arrowPlacement="end"
            >
              <AlertContent>
                <StyledAlertTitle variant="s2" className={alert.siteImpact}>
                  {alert.shortDescription}
                </StyledAlertTitle>
                <StyledTitle>Issue</StyledTitle>
                <StyledText variant="b4">{alert.description}</StyledText>
                <StyledTitle>Effect</StyledTitle>
                <StyledText variant="b4">{alert.effect}</StyledText>
                <StyledTitle>Solution</StyledTitle>
                <StyledText variant="b4">
                  {alert.solution} {alert.kb}
                </StyledText>
              </AlertContent>
            </Accordion>
          ))}
        </StyledDetails>
      </StyledGrid> */}
    </StyledContainer>
  );
}
